import React, { FC, useCallback, useContext, useEffect } from 'react';
import { ListItem, ListItemIcon, ListItemText } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { Link, useHistory } from 'react-router-dom';
import { matchPath } from 'react-router';
import { combineCn } from '../../helpers/combineCn';
import { FetcherInvalidatorContext } from '../../contexts/FetcherInvalidator';
import { AuthenticatedRoutes } from '../../routers/AuthenticatedRoutes';
import { ArgsType } from '../../types/ArgsType';
import { useIsGuardedRouteAllowed } from '../../resources/GuardedRouteLink';

interface IProps {
  route: () => string;
  label: string;
  icon: React.ComponentType;
  match?: string[];
}

const useStyles = makeStyles((theme) => {
  return createStyles({
    active: {
      color: theme.palette.primary.main,
      '& svg': {
        color: theme.palette.primary.main,
      },
      '&:after': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        width: 4,
        height: '100%',
        background: theme.palette.primary.main,
      },
    },
  });
});

export const DashboardDrawerItem: FC<IProps> = ({ icon: Icon, label, route, match }) => {
  const history = useHistory();
  const styles = useStyles();
  const { trigger: triggerInvalidator } = useContext(FetcherInvalidatorContext);

  const isActive =
    matchPath(history.location.pathname, route()) ||
    (match && match.some((matchUrl) => matchPath(history.location.pathname, matchUrl)));

  const onClick = useCallback(() => {
    if (isActive) triggerInvalidator();
  }, [isActive]);

  useEffect(() => {
    if (isActive) {
      const titleEl = document.head.querySelector('title');
      if (!titleEl) return;
      titleEl.innerText = `Pixotope Cloud | ${label}`;
      return () => {
        titleEl.innerText = 'Pixotope Cloud';
      };
    }
  }, [isActive]);

  return (
    <ListItem
      button
      component={Link}
      to={route()}
      className={combineCn({ [styles.active]: !!isActive })}
      onClick={onClick}
    >
      <ListItemIcon>
        <Icon />
      </ListItemIcon>
      <ListItemText primary={label} />
    </ListItem>
  );
};

interface IGuardedProps<RouteName extends keyof typeof AuthenticatedRoutes> extends Omit<IProps, 'route'> {
  route: RouteName;
  params: ArgsType<typeof AuthenticatedRoutes[RouteName]>;
}

export const DashboardGuardedDrawerItem = <RouteName extends keyof typeof AuthenticatedRoutes>({
  route,
  params,
  ...props
}: IGuardedProps<RouteName>) => {
  const isAllowed = useIsGuardedRouteAllowed(route, params);
  return isAllowed ? <DashboardDrawerItem route={AuthenticatedRoutes[route]} {...props} /> : null;
};
